import { AxiosResponse } from 'axios';
import apiClient from './apiClient';

// Types for discount codes
export interface DiscountCode {
  id: number;
  code: string;
  description: string;
  discountType: 'fixed' | 'percentage';
  discountValue: number;
  maxUses: number | null;
  usedCount: number;
  isActive: boolean;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDiscountCodeData {
  code: string;
  description: string;
  discountType: 'fixed' | 'percentage';
  discountValue: number;
  maxUses?: number | null;
  isActive?: boolean;
  expiresAt?: string | null;
}

export interface UpdateDiscountCodeData {
  description?: string;
  discountType?: 'fixed' | 'percentage';
  discountValue?: number;
  maxUses?: number | null;
  isActive?: boolean;
  expiresAt?: string | null;
}

export interface ValidateDiscountCodeResponse {
  success: boolean;
  data?: {
    code: string;
    discountType: 'fixed' | 'percentage';
    discountValue: number;
    discount: number;
    originalAmount: number;
    finalAmount: number;
  };
  message?: string;
}

// Validate and apply a discount code
export const validateDiscountCode = async (code: string, amount: number): Promise<ValidateDiscountCodeResponse> => {
  try {
    const response: AxiosResponse<ValidateDiscountCodeResponse> =
      await apiClient.post('/api/discount-codes/validate', { code, amount });
    return response.data;
  } catch (error: unknown) {
    console.error('Error validating discount code:', error);
    const err = error as { response?: { data?: ValidateDiscountCodeResponse } };
    if (err?.response?.data) {
      return err.response.data;
    }
    throw error;
  }
};

// Get all discount codes (admin only)
export const getAllDiscountCodes = async (activeOnly = false): Promise<DiscountCode[]> => {
  try {
    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();
    const response: AxiosResponse<{ success: boolean, data: DiscountCode[] }> = 
      await apiClient.get(`/api/discount-codes?activeOnly=${activeOnly}&_t=${timestamp}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching discount codes:', error);
    throw error;
  }
};

// Get discount code by ID (admin only)
export const getDiscountCodeById = async (id: number): Promise<DiscountCode> => {
  try {
    const response: AxiosResponse<{ success: boolean, data: DiscountCode }> = 
      await apiClient.get(`/api/discount-codes/${id}`);
    return response.data.data;
  } catch (error) {
    console.error(`Error fetching discount code ${id}:`, error);
    throw error;
  }
};

// Create a new discount code (admin only)
export const createDiscountCode = async (codeData: CreateDiscountCodeData): Promise<DiscountCode> => {
  try {
    const response: AxiosResponse<{ success: boolean, data: DiscountCode }> = 
      await apiClient.post('/api/discount-codes', codeData);
    return response.data.data;
  } catch (error) {
    console.error('Error creating discount code:', error);
    throw error;
  }
};

// Update a discount code (admin only)
export const updateDiscountCode = async (id: number, codeData: UpdateDiscountCodeData): Promise<DiscountCode> => {
  try {
    const response: AxiosResponse<{ success: boolean, data: DiscountCode }> = 
      await apiClient.put(`/api/discount-codes/${id}`, codeData);
    return response.data.data;
  } catch (error) {
    console.error(`Error updating discount code ${id}:`, error);
    throw error;
  }
};

// Delete a discount code (admin only)
export const deleteDiscountCode = async (id: number): Promise<boolean> => {
  try {
    const response: AxiosResponse<{ success: boolean, message: string }> = 
      await apiClient.delete(`/api/discount-codes/${id}`);
    return response.data.success;
  } catch (error) {
    console.error(`Error deleting discount code ${id}:`, error);
    throw error;
  }
};